import { isEmpty } from 'lodash';
import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import LanguageOutlinedIcon from '@mui/icons-material/LanguageOutlined';
import MoreHorizOutlinedIcon from '@mui/icons-material/MoreHorizOutlined';
import ReportOutlinedIcon from '@mui/icons-material/ReportOutlined';
import ThumbUpOutlinedIcon from '@mui/icons-material/ThumbUpOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import { Avatar } from '@mui/material';

import Label from 'pages/Search/components/Database/components/Label';
import Score from 'pages/Search/components/Database/components/Score';
import Tag from 'pages/Search/components/Database/components/Tag';
import useFormatDate from 'pages/Search/components/Database/hooks/use-format-date';
import { FormattedResponseItem } from 'pages/Search/components/Database/types';

import useStyles from './styles';

interface Props {
    data: FormattedResponseItem;
    className?: string;
}

const SearchResultItem: React.FC<Props> = ({ data, className }) => {
    const { classes, cx } = useStyles();
    const formatDate = useFormatDate();
    const [isExpanded, setIsExpanded] = useState(data.exact);
    const hasLabels = useMemo(() => {
        const {
            exact,
            isWild,
            references,
            exploits,
            affectedSoftware,
            affectedPackages,
            affectedConfiguration,
            webApplicable
        } = data;
        return [
            exact,
            isWild,
            references,
            exploits,
            affectedSoftware,
            affectedPackages,
            affectedConfiguration,
            webApplicable
        ].some((item) => !!item && !isEmpty(item));
    }, [data]);

    return (
        <Link to={data.href} className={cx(classes.container, { [classes.exact]: data.exact }, className)}>
            <div className={classes.content}>
                <div className={classes.header}>
                    <span className={classes.type}>
                        <Avatar className={classes.avatar} src={data.image} alt={data.type} />
                        {data.type}
                    </span>
                    <span className={classes.dot}>{'•'}</span>
                    <span className={classes.published}>{`added ${formatDate(data.published)}`}</span>
                    <span className={classes.dot}>{'•'}</span>
                    <span className={classes.views}>
                        <VisibilityOutlinedIcon />
                        {`${data.views} views`}
                    </span>
                </div>
                <div className={classes.text}>
                    <p className={classes.title}>{data.title}</p>
                    <p className={classes.description}>{data.description}</p>
                </div>
                <div className={classes.scores}>
                    {data.cvssScore ? <Score value={data.cvssScore} label="CVSS" /> : null}
                    {data.aiScore ? <Score value={data.aiScore} label="AI score" /> : null}
                    {data.epssScore ? <Score value={data.epssScore} label="EPSS" scale={0.1} /> : null}
                    {isEmpty(data.tags) || isExpanded ? null : (
                        <MoreHorizOutlinedIcon
                            className={classes.more}
                            onClick={(event) => {
                                event.preventDefault();
                                event.stopPropagation();
                                setIsExpanded(true);
                            }}
                        />
                    )}
                </div>
                {data.tags.length && isExpanded ? (
                    <div className={classes.tags}>
                        {data.tags.map((label, index) => (
                            <Tag key={index} label={label} />
                        ))}
                    </div>
                ) : null}
            </div>
            {hasLabels ? (
                <div className={classes.labels}>
                    {data.exact || data.isWild || data.webApplicable ? (
                        <div className={classes.special}>
                            {data.exact ? <Label icon={<ThumbUpOutlinedIcon />} text="Match" /> : null}
                            {data.isWild ? (
                                <Label icon={<ReportOutlinedIcon />} variant="danger" text="In wild" />
                            ) : null}
                            {data.webApplicable ? <Label icon={<LanguageOutlinedIcon />} text="Web" /> : null}
                        </div>
                    ) : null}
                    {data.references ? <Label text="References" count={data.references} /> : null}
                    {data.exploits ? <Label text="Exploits" count={data.exploits} /> : null}
                    {data.affectedSoftware ? <Label text="Affected Software" count={data.affectedSoftware} /> : null}
                    {data.affectedPackages ? <Label text="Affected Packages" count={data.affectedPackages} /> : null}
                    {data.affectedConfiguration ? (
                        <Label text="Affected Configuration" count={data.affectedConfiguration} />
                    ) : null}
                </div>
            ) : null}
        </Link>
    );
};

export default SearchResultItem;
